import {
  Alert,
  Drawer,
  Empty,
  Pagination,
  Spin,
  Table,
  Tag,
} from "@arco-design/web-react";
import type { TableColumnProps } from "@arco-design/web-react";
import { useMemo } from "react";

import type {
  CustomizationProjectItineraryItem,
  CustomizationProjectItineraryList,
} from "../types";
import { formatBeijingCompactTime } from "./customizationProjectListTime";

type Props = {
  visible: boolean;
  fallbackProjectName: string;
  data: CustomizationProjectItineraryList | null;
  loading: boolean;
  errorMessage: string;
  onClose: () => void;
  onPageChange: (page: number) => void;
  onPreviewItinerary: (itineraryId: string) => void;
};

const currencyFormatter = new Intl.NumberFormat("zh-CN", {
  style: "currency",
  currency: "CNY",
});

/** 行程抽屉表格列宽合计，供布局与文档一致。 */
export const CUSTOMIZATION_ITINERARY_DRAWER_SCROLL_WIDTH = 220 + 130 + 150 + 110 + 110 + 90 + 140 + 140;

/** 展示定制项目全部行程的固定分页业务汇总。 */
export function CustomizationProjectItineraryDrawer({
  visible,
  fallbackProjectName,
  data,
  loading,
  errorMessage,
  onClose,
  onPageChange,
  onPreviewItinerary,
}: Props) {
  const title = (
    <div className="customization-itinerary-drawer-title">
      <strong>{data?.projectName || fallbackProjectName || "全部行程"}</strong>
      <span>定制号：{data?.projectNumber || "--"}</span>
    </div>
  );
  const columns = useMemo(
    () => buildItineraryColumns(onPreviewItinerary),
    [onPreviewItinerary],
  );

  return (
    <Drawer
      className="customization-project-itinerary-drawer"
      wrapClassName="customization-project-itinerary-drawer-wrapper"
      title={title}
      width="min(1120px, calc(100vw - 24px))"
      visible={visible}
      footer={null}
      zIndex={1200}
      getPopupContainer={() => document.body}
      onCancel={onClose}
    >
      <div className="customization-itinerary-drawer-body">
        {errorMessage ? <Alert type="error" showIcon content={errorMessage} /> : null}
        <Spin loading={loading} style={{ width: "100%", display: "block" }}>
          {data && data.items.length > 0 ? (
            <div className="customization-itinerary-drawer-panel">
              <div className="customization-itinerary-drawer-table-wrap">
                <div className="customization-itinerary-drawer-table-inner">
                  <Table
                    className="customization-itinerary-drawer-table"
                    rowKey="id"
                    size="small"
                    columns={columns}
                    data={data.items}
                    border={{ wrapper: true, cell: false }}
                    pagination={false}
                  />
                </div>
              </div>
              <div className="customization-itinerary-drawer-pagination">
                <Pagination
                  current={data.page}
                  pageSize={8}
                  total={data.total}
                  sizeCanChange={false}
                  showTotal
                  onChange={onPageChange}
                />
              </div>
            </div>
          ) : !loading && !errorMessage ? (
            <Empty description="暂无行程" />
          ) : null}
        </Spin>
      </div>
    </Drawer>
  );
}

/** 构建行程抽屉列，标题可打开统一预览。 */
function buildItineraryColumns(
  onPreviewItinerary: (itineraryId: string) => void,
): TableColumnProps<CustomizationProjectItineraryItem>[] {
  return [
    {
      title: "行程名称",
      dataIndex: "title",
      width: 220,
      render: (_, item) => {
        const titleText = item.title || "未命名行程";
        return (
          <button
            type="button"
            className="customization-itinerary-drawer-title-link"
            aria-label={`打开行程 ${titleText}`}
            onClick={() => onPreviewItinerary(item.id)}
          >
            {titleText}
          </button>
        );
      },
    },
    {
      title: "所属定制版本",
      dataIndex: "demandVersionNumber",
      width: 130,
      render: (_, item) => item.demandVersionNumber || "--",
    },
    {
      title: "行程修改时间",
      dataIndex: "updatedAt",
      width: 150,
      render: (_, item) => formatBeijingCompactTime(item.updatedAt),
    },
    {
      title: "已定稿核价",
      dataIndex: "finalizedBudgetCount",
      width: 110,
    },
    {
      title: "已定稿报价",
      dataIndex: "finalizedQuoteCount",
      width: 110,
    },
    {
      title: "成团",
      dataIndex: "isFormed",
      width: 90,
      render: (_, item) => (
        <Tag color={item.isFormed ? "red" : "gray"}>{item.isFormed ? "已成团" : "未成团"}</Tag>
      ),
    },
    {
      title: "报价价格",
      dataIndex: "latestFinalizedQuoteTotalAmount",
      width: 140,
      render: (_, item) => {
        if (item.isFormed) {
          return "金额暂不显示";
        }
        return item.latestFinalizedQuoteTotalAmount
          ? formatCurrency(item.latestFinalizedQuoteTotalAmount)
          : "暂无定稿报价";
      },
    },
    {
      title: "核价价格",
      dataIndex: "linkedFinalizedBudgetTotalAmount",
      width: 140,
      render: (_, item) => {
        if (item.isFormed) {
          return "金额暂不显示";
        }
        return item.linkedFinalizedBudgetTotalAmount
          ? formatCurrency(item.linkedFinalizedBudgetTotalAmount)
          : "--";
      },
    },
  ];
}

/** 格式化后端 Decimal 字符串，非法值保守显示占位。 */
function formatCurrency(value: string): string {
  const amount = Number(value);
  return Number.isFinite(amount) ? currencyFormatter.format(amount) : "--";
}
